import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import type { SuggestedApp } from '../../types';
import { getAccessibleApp } from '../../config/appRegistry';
import { logger } from '../../utils/logger';
import './AppHandoffCard.css';

interface AppHandoffCardProps {
  suggestedApp?: SuggestedApp | null;
  /** Correlation id of the turn that produced the suggestion, for click telemetry. */
  queryId?: string;
  onOpen?: (appKey: string) => void;
}

/**
 * "That's an InsightManager question" -- the warm handoff into an internal app.
 *
 * Renders next to the assistant's answer, never instead of it. The point is to
 * put the user in front of the app that owns their question and to advertise
 * what else lives there.
 *
 * ACCESS: re-resolves the suggestion through getAccessibleApp() on every render
 * rather than trusting the message payload. Messages are persisted to
 * localStorage, so a card can be rehydrated long after the AD group that
 * justified it was revoked -- and a card whose link bounces the user at sign-in
 * is worse than no card. Renders nothing when access cannot be confirmed.
 */
export const AppHandoffCard = ({ suggestedApp, queryId, onOpen }: AppHandoffCardProps) => {
  const entry = getAccessibleApp(suggestedApp?.key);
  if (!entry) return null;

  const handleOpen = () => {
    // Fire-and-forget: a telemetry failure must never block the handoff, which
    // is the whole point of the card.
    try {
      onOpen?.(entry.key);
    } catch (error) {
      logger.warn('App handoff telemetry failed:', error);
    }
    window.open(entry.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <motion.div
      className="app-handoff-card"
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      role="complementary"
      aria-label={`Suggested app: ${entry.label}`}
    >
      <div className="app-handoff-body">
        <span className="app-handoff-label">Better answered in</span>
        <span className="app-handoff-name">{entry.label}</span>
        <p className="app-handoff-blurb">{entry.blurb}</p>
      </div>
      <button
        type="button"
        className="app-handoff-action"
        onClick={handleOpen}
        data-query-id={queryId}
      >
        Open {entry.label}
        <ArrowUpRight size={14} aria-hidden="true" />
      </button>
    </motion.div>
  );
};

export default AppHandoffCard;
